<template>
  <div>
    <cb-details
      v-if="getterPopupName === 'UpiCbTxnDetails'"
      :txnDetails="popupDetails"
    />
    <cb-filter
      v-else-if="getterPopupName === 'UpiCbTxnFilter'"
      @updateFilters="updateFilters"
    />
    <cb-export
      v-else-if="getterPopupName === 'UpiCbTxnExport'"
      :exportOptions="paginate"
      :exportRecords="exportRecords"
    />

    <div class="table-title">
      <div class="subject">
        <p>Connected Banking</p>
        <span v-if="typeof paginate.total === 'number'">
          Transactions ({{paginate.total}})
        </span>

        <cb-webhooks
          v-if="paginate.total && webhooksAllowed && !searchTerm"
          :paginate="paginate"
          @resendWebhooks="bulkResendWebhooks"
        />
      </div>

      <div class="filters">
        <div class="options">
          <button @click="openPopup('UpiCbTxnExport')">
            Export
            <i class="fa fa-share" />
          </button>
          <button
            :class="{'filters-applied': filtersApplied}"
            @click="openPopup('UpiCbTxnFilter')"
            class="dark-btn"
          >
            Filter
            <i class="fa fa-filter" />
          </button>
        </div>

        <cb-search
          placeholder="Search ID / Bank ref number / Payee VPA"
          :triggerSearch="(init) => triggerSearchPaginate(false, init)"
          @populateList="clearSearch"
          :searchTerm="searchTerm"
          @setFunctions="setSearchFunctions"
        />
      </div>
    </div>

    <div class="table-head" v-if="isDesktop()">
      <p>ID</p>
      <p class="d-l">Time</p>
      <p class="d-l">Payee VPA</p>
      <p>Payment Mode</p>
      <p class="d-l">Bank Ref Num</p>
      <p>Transaction Amount</p>
      <p>Fees &amp; GST</p>
      <p>Settlement Amount</p>
    </div>

    <div class="table-body">
      <div class="table-row cursor"
        v-for="(txn, index) in list" :key="index"
        @click="showDetails(txn)"
      >
        <p>
          <b v-if="!isDesktop()">ID:</b>
          <span>{{txn.id}}</span>
        </p>
        <p class="mt-grey-text d-l">
          {{txn.created_at}}
        </p>
        <p class="d-l">
          <b v-if="!isDesktop()">Payee VPA:</b>
          <span>{{txn.payee_vpa}}</span>
        </p>
        <p>
          <b v-if="!isDesktop()">Pymt Mode:</b>
          {{txn.payment_type}}
        </p>
        <p class="d-l">
          <b v-if="!isDesktop()">Bank Ref Num:</b>
          <span>{{txn.bank_ref_num}}</span>
        </p>
        <p>
          <b v-if="!isDesktop()">Txn Amt:</b>
          {{txn.amount}}
        </p>
        <p>
          <b v-if="!isDesktop()">Fees &amp; GST:</b>
          {{txn.charges_gst}}
        </p>
        <p>
          <b v-if="!isDesktop()">Settlement Amt:</b>
          {{txn.settled_amount}}
        </p>
      </div>
    </div>

    <table-loader v-if="!paginate.data_added || list.length < paginate.total" />

    <div class="table-empty" v-else-if="paginate.total === 0">
      <p v-if="searchTerm">Unable to find transactions with <span class="highlight">{{searchTerm}}</span>.<br>Please search with the complete ID / Bank Reference Number / Virtual Account Number.<br>Search is case sensitive - please search with the correct case to get the valid results.</p>
      <p v-else>{{emptyRecords}}</p>
    </div>
  </div>
</template>

<script>
import CbWebhooks from '@/components/global/BulkResendWebhooks'
import CbExport from '@/components/global/ExportConfirmation'
import CbSearch from '@/components/global/SearchRecords'
import CbDetails from '@/components/AccountStatement/StatementDetails'
import CbFilter from '@/components/UPIs/UpiStatementFilter'

import ApiServices from '@/services/api/upiConnectedBanking'
import listMixins from '@/mixins/listMixins'
import { mapMutations } from 'vuex'

export default {
  name: 'UPIConnectedBanking',
  components: { CbDetails, CbFilter, CbSearch, CbExport, CbWebhooks },
  mixins: [listMixins],
  created () {
    this.startProcessing()
  },
  methods: {
    ...mapMutations(['SET_UPI_CONNECTED_BANKING']),
    populateParams () {
      this.api['export'] = ApiServices.export
      this.api['search'] = ApiServices.search
      this.api['bulkResendWebhook'] = ApiServices.bulkResendWebhook
      this.keys['list'] = 'transactions'
      this.keys['popup'] = 'UpiCbTxnDetails'
      this.setHighlightSelectors([1, 3, 5])
    },
    fetchRecords (start) {
      return new Promise(resolve => {
        let params = {
          ...this.filterOptions
        }
        if (start) {
          params['offset'] = start
          params['filter_token'] = this.paginate.filter_token
        }

        ApiServices.list(params).then((response) => {
          this.SET_STATEMENT_FILTER(this.filterOptions)
          let data = response.data
          if (data) {
            if (start) {
              this.list.push(...data.transactions)
            } else {
              this.list = data.transactions
              this.setPaginate(data)
              this.populateMeta(data)
            }
            resolve()
          } else if (response.reason === 'CONNECTED_BANKING_INACTIVE') {
            this.SET_UPI_CONNECTED_BANKING(false)
            this.$router.push({name: 'UPIList'})
          }
        })
      })
    },
    populateMeta (data) {
      this.webhooksAllowed = data.webhook_allowed
      this.SET_UPI_CONNECTED_BANKING(true)
    },
    fetchSearchRecords (params, start, initial) {
      return new Promise(resolve => {
        ApiServices.search(params).then((response) => {
          let data = response.data
          if (data) {
            if (start) {
              this.list.push(...data.transactions)
              this.postSearch(false, this.paginate.total)
            } else {
              this.list = data.transactions
              this.paginate['start'] = 0
              this.paginate['total'] = data.total
              this.paginate['page_size'] = data.page_size
              this.paginate['filter_token'] = data.filter_token
              this.paginate['data_added'] = true

              this.postSearch(initial, this.paginate.total)
              if (initial) {
                this.populateMeta(response, true)
              }
            }
            resolve()
          } else if (response.reason === 'CONNECTED_BANKING_INACTIVE') {
            this.SET_UPI_CONNECTED_BANKING(false)
            this.$router.push({name: 'UPIList'})
          }
        }).catch(() => {
          this.list = []
          this.paginate['total'] = 0
          this.paginate['data_added'] = true
        })
      })
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/table.scss';

  .mobile, .tablet {
    .table-row {
      p {
        white-space: unset !important;

        &:nth-child(even) {
          text-align: right;
        }
      }
    }
  }
</style>
